import { pageBackgroundStyle } from '@ui/components/Background/PageBackground';
import { BigIntAmount } from '@ui/components/BigIntAmount/BigIntAmount';
import { BigIntAmountUsd } from '@ui/components/BigIntAmount/BigIntAmountUsd';
import { Copy, Info, LinkExternal, ShareLink, X } from '@ui/components/icons';
import { TextTooltip } from '@ui/components/TextTooltip';
import { basicToaster } from '@ui/components/Toast';
import { useCreateCode } from '@ui/features/GenesisAirdrop/useCreateCode';
import { useJoinTeam } from '@ui/features/GenesisAirdrop/useJoinTeam';
import { RequireConnectionWrapper } from '@ui/features/Wallet/components/RequireConnectionWrapper';
import { useDataChainId } from '@ui/hooks/datachain/useDataChainStore';
import { TopNavBar } from '@ui/layouts/components/TopNavBar';
import { formatBigValue } from '@ui/lib/bignumber';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Input } from '@ui/lib/park/ui/input';
import { Pagination } from '@ui/lib/park/ui/pagination';
import { Tabs } from '@ui/lib/park/ui/tabs';
import { Text } from '@ui/lib/park/ui/text';
import { useTimeUntilNextDayEpoch } from '@ui/lib/timer/hooks/useTimeUntilNextDayEpoch';
import { useTeamBoostHistoryQuery } from '@ui/services/backend/hooks/genesisAirdrop/useTeamBoostHistoryQuery';
import { useTeamLeaderboardQuery } from '@ui/services/backend/hooks/genesisAirdrop/useTeamLeaderboardQuery';
import { useTeamMemberQuery } from '@ui/services/backend/hooks/genesisAirdrop/useTeamMemberQuery';
import {
  getContractAddress,
  getTokenAddress,
} from '@ui/services/contract/constants/address';
import { calculateTeamBoost } from '@ui/services/contract/hooks/GenesisAirdrop/points';
import { useGenesisAirdropUIData } from '@ui/services/contract/hooks/GenesisAirdrop/useData';
import { useUserTeamInfo } from '@ui/services/contract/hooks/GenesisAirdrop/useUserTeamInfo';
import { shortenAddress } from '@ui/utils/formatAddress';
import { formatDate } from '@ui/utils/formatDate';
import { formatDurationShort } from '@ui/utils/formatDurationShort';
import { getExplorerLink } from '@ui/utils/links';
import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import {
  Box,
  type BoxProps,
  Flex,
  Grid,
  HStack,
  Stack,
  styled,
} from 'styled-system/jsx';
import { parseUnits, zeroAddress } from 'viem';
import { useAccount } from 'wagmi';

const teamDataDefault = {
  teamName: '',
  code: '',
  teamMemberCount: 0,
  latestEpochTrading: {
    epoch: 0,
    volumeUSD: '0',
  },
  totalGeneralPoints: '0',
  totalDegenPoints: '0',
};

/* ---------------------------------------------------------------- */
/* V2 shared primitives (view-local)                                */
/* ---------------------------------------------------------------- */

const Panel = (props: BoxProps) => (
  <Box
    bg="v2.bg.panel"
    border="1px solid"
    borderColor="v2.border.default"
    borderRadius="4px"
    overflow="hidden"
    {...props}
  />
);

const SectionLabel = ({
  children,
  ...props
}: BoxProps & { children: React.ReactNode }) => (
  <Box textStyle="v2.sectionLabel" color="v2.text.subtle" {...props}>
    {children}
  </Box>
);

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex
    justify="space-between"
    align="center"
    minH="34px"
    px="16px"
    py="4px"
    gap={3}
    borderBottom="1px solid"
    borderBottomColor="v2.border.hairline"
    _last={{ borderBottom: 'none' }}
  >
    <Text textStyle="v2.label" color="v2.text.muted" flexShrink={0}>
      {label}
    </Text>
    <Box
      textStyle="numbers.small"
      color="v2.text.primary"
      textAlign="right"
      minW={0}
      css={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {children}
    </Box>
  </Flex>
);

const InviteCodeChip = ({ code }: { code: string }) => {
  const onCopy = async () => {
    try {
      const origin = window.location.origin;
      await navigator.clipboard.writeText(
        `${origin}?code=${encodeURIComponent(code)}`,
      );
      basicToaster.success({ title: 'Copied to clipboard' });
    } catch {}
  };

  return (
    <HStack
      display="inline-flex"
      gap="7px"
      bg="v2.bg.surface1"
      border="1px solid"
      borderColor="v2.border.default"
      borderRadius="2px"
      px="9px"
      py="4px"
    >
      <Text textStyle="numbers.xsmall" color="v2.long.default">
        {code}
      </Text>
      <styled.button
        type="button"
        aria-label="Copy invite link"
        display="flex"
        color="v2.text.subtle"
        cursor="pointer"
        _hover={{ color: 'v2.text.primary' }}
        onClick={onCopy}
      >
        <Icon boxSize="12px">
          <Copy />
        </Icon>
      </styled.button>
    </HStack>
  );
};

const GhostLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <styled.a
    href={href}
    target="_blank"
    rel="noreferrer"
    display="inline-flex"
    alignItems="center"
    gap="6px"
    h="30px"
    px="12px"
    borderRadius="2px"
    border="1px solid"
    borderColor="v2.border.default"
    color="v2.text.muted"
    textStyle="v2.label"
    whiteSpace="nowrap"
    cursor="pointer"
    _hover={{ borderColor: 'v2.border.strong', color: 'v2.text.primary' }}
  >
    {children}
    <Icon boxSize="11px">
      <LinkExternal />
    </Icon>
  </styled.a>
);

const RankChip = ({ rank }: { rank: number }) => (
  <Flex
    align="center"
    justify="center"
    w="22px"
    h="22px"
    borderRadius="2px"
    flexShrink={0}
    fontFamily="Inter"
    fontSize="11px"
    fontWeight="600"
    css={{ fontVariantNumeric: 'tabular-nums' }}
    bg={
      rank === 1
        ? 'v2.long.default'
        : rank <= 3
          ? 'v2.long.dim'
          : 'v2.bg.surface2'
    }
    color={
      rank === 1
        ? 'v2.text.onAccent'
        : rank <= 3
          ? 'v2.long.default'
          : 'v2.text.muted'
    }
    border={rank === 2 || rank === 3 ? '1px solid' : undefined}
    borderColor="v2.long.border"
  >
    {rank}
  </Flex>
);

const tableHeaderCellCss = {
  textStyle: 'v2.tableHeader',
  color: 'v2.text.subtle',
  whiteSpace: 'nowrap',
} as const;

/* ---------------------------------------------------------------- */
/* Page                                                             */
/* ---------------------------------------------------------------- */

export const Page: NextPage = () => {
  const { t } = useTranslation();
  const { address } = useAccount();
  const { data } = useGenesisAirdropUIData(address);
  const { data: userTeamInfo } = useUserTeamInfo(address);
  const timeUntilNextDayEpoch = useTimeUntilNextDayEpoch();
  const chainId = useDataChainId();
  const lvAddress = getTokenAddress('LV', chainId);
  const lvExplorerUrl = lvAddress
    ? getExplorerLink(lvAddress, 'token', chainId)
    : undefined;
  const generalGenesisPointsAddress = getContractAddress(
    'GeneralGenesisPoints',
    chainId,
  );
  const degenGenesisPointsAddress = getContractAddress(
    'DegenGenesisPoints',
    chainId,
  );

  const createCodeMinVolume = parseUnits('10000', 18);
  const userVolumeUSD = data?.userData.userCumulativeVolumeUSD || 0n;

  const myGeneralPoint = data?.userData.userGeneralPoints || 0n;
  const myHighLeveragePoint = data?.userData.userDegenPoints || 0n;

  const totalGeneralPoint = data?.pointsData.totalGeneralPoints || 0n;
  const totalHighLeveragePoint = data?.pointsData.totalDegenPoints || 0n;

  const teamBoostGeneralClaimable =
    data?.userData.teamBoostGeneralClaimable || 0n;
  const teamBoostDegenClaimable = data?.userData.teamBoostDegenClaimable || 0n;
  const noClaimablePreviousBoost =
    teamBoostGeneralClaimable === 0n && teamBoostDegenClaimable === 0n;

  const leaderTeamData = userTeamInfo?.leaderTeam || teamDataDefault;
  const memberTeamData = userTeamInfo?.memberTeam || teamDataDefault;

  const isLeaderTeamCreated = leaderTeamData.teamMemberCount > 0;
  const isMemberTeamJoined = memberTeamData.teamMemberCount > 0;

  const currentEpoch = data?.currentEpoch || 0;

  const basicInfoRows = (
    teamData: typeof teamDataDefault,
    hasTeam: boolean,
  ) => (
    <Stack gap={0}>
      <InfoRow label="Team Name">
        {hasTeam ? teamData.teamName : 'No data'}
      </InfoRow>
      <InfoRow label="Invite Code">
        {hasTeam && teamData.code ? (
          <InviteCodeChip code={teamData.code} />
        ) : (
          'No data'
        )}
      </InfoRow>
      <InfoRow label="Total General Points">
        {hasTeam ? (
          <BigIntAmount
            opts={{
              displayDecimals: 2,
              decimals: 2,
            }}
            value={BigInt(teamData.totalGeneralPoints)}
          />
        ) : (
          'No data'
        )}
      </InfoRow>
      <InfoRow label="Total High-Leverage Points">
        {hasTeam ? (
          <BigIntAmount
            opts={{
              displayDecimals: 2,
              decimals: 2,
            }}
            value={BigInt(teamData.totalDegenPoints)}
          />
        ) : (
          'No data'
        )}
      </InfoRow>
      <InfoRow label="Current Epoch Volume">
        {hasTeam && teamData.latestEpochTrading.epoch == currentEpoch ? (
          <BigIntAmountUsd
            opts={{
              displayDecimals: 0,
              kmb: true,
            }}
            value={BigInt(teamData.latestEpochTrading.volumeUSD)}
          />
        ) : (
          'No data'
        )}
      </InfoRow>
      <InfoRow label="Team Boost (current epoch)">
        {hasTeam && teamData.latestEpochTrading.epoch == currentEpoch
          ? `${calculateTeamBoost(
              BigInt(teamData.latestEpochTrading.volumeUSD),
            )}×`
          : 'No data'}
      </InfoRow>
      <InfoRow label="Team Members">
        {hasTeam ? teamData.teamMemberCount : 'No data'}
      </InfoRow>
    </Stack>
  );

  return (
    <Flex direction="column" minHeight="100vh" style={pageBackgroundStyle}>
      <TopNavBar />

      <Flex
        direction="column"
        w="100%"
        maxW="1120px"
        mx="auto"
        px={{ base: '12px', tablet: '24px' }}
        pt={{ base: '20px', tablet: '28px' }}
        pb={{ base: '32px', tablet: '48px' }}
        gap={{ base: '12px', tablet: '16px' }}
      >
        {/* ---- Page head ---- */}
        <Flex
          align="flex-start"
          gap="14px"
          flexWrap={{ base: 'wrap', desktop: 'nowrap' }}
        >
          <styled.img
            src="/tokens/LV.svg"
            alt="$LV"
            w="40px"
            h="40px"
            flexShrink={0}
          />
          <Flex direction="column" gap="3px" minW={0}>
            <Flex align="center" gap="10px" flexWrap="wrap">
              <Text
                as="h1"
                textStyle="headings.xxlarge"
                fontSize="24px"
                letterSpacing="-0.015em"
                color="v2.text.primary"
              >
                {t('airdrop.head.title', 'LV Genesis Airdrop')}
              </Text>
              <styled.span
                fontFamily="Inter"
                fontSize="11px"
                fontWeight="500"
                letterSpacing="0.01em"
                color="v2.long.default"
                bg="v2.long.dim"
                border="1px solid"
                borderColor="v2.long.border"
                borderRadius="2px"
                p="3px 8px"
                whiteSpace="nowrap"
              >
                Season 1 · Final
              </styled.span>
              {lvExplorerUrl && (
                <TextTooltip
                  content="View on Explorer"
                  triggerCss={{
                    textDecoration: 'none',
                  }}
                  contentCss={{
                    p: '4px 6px',
                  }}
                >
                  <Icon
                    onClick={() => {
                      window.open(lvExplorerUrl, '_blank');
                    }}
                    cursor="pointer"
                    boxSize="14px"
                    color="v2.text.subtle"
                    _hover={{ color: 'v2.text.primary' }}
                  >
                    <LinkExternal />
                  </Icon>
                </TextTooltip>
              )}
            </Flex>
            <Text fontSize="13px" color="v2.text.muted" maxW="64ch">
              Join the $LV Genesis Airdrop. Earn points by{' '}
              <styled.span color="v2.text.secondary" fontWeight="600">
                Trading
              </styled.span>{' '}
              and{' '}
              <styled.span color="v2.text.secondary" fontWeight="600">
                Referrals
              </styled.span>
              . Your total points determine your share of $LV rewards at
              distribution.
            </Text>
          </Flex>
          <Flex
            gap="8px"
            flexShrink={0}
            ml={{ desktop: 'auto' }}
            w={{ base: '100%', desktop: 'auto' }}
            flexWrap="wrap"
          >
            <GhostLink href="https://leverup.gitbook.io/docs/liquidity-layer/usdlv-point-system/point-fomula">
              Point formula
            </GhostLink>
            <GhostLink href="https://medium.com/@leverup_xyz/transparency-notice-lv-on-chain-airdrop-652b3fbf89d3">
              Season 0 distribution
            </GhostLink>
          </Flex>
        </Flex>

        {/* ---- Points strip ---- */}
        <Panel>
          <Grid gridTemplateColumns={{ base: '1fr', tablet: '1fr 1fr' }} gap={0}>
            <PointsCell
              iconSrc="/icons/general-point.svg"
              title="My General Points"
              desc="Total Points pool"
              value={formatBigValue(myGeneralPoint, {
                decimals: 2,
                displayDecimals: 2,
              })}
              totalValue={formatBigValue(totalGeneralPoint, {
                decimals: 2,
                displayDecimals: 2,
              })}
              explorerUrl={
                generalGenesisPointsAddress
                  ? getExplorerLink(
                      generalGenesisPointsAddress,
                      'token',
                      chainId,
                    )
                  : undefined
              }
            />
            <Box
              borderLeft={{ tablet: '1px solid' }}
              borderLeftColor={{ tablet: 'v2.border.default' }}
              borderTop={{ base: '1px solid', tablet: 'none' }}
              borderTopColor="v2.border.default"
            >
              <PointsCell
                iconSrc="/icons/high-leverage.svg"
                title="My High-Leverage Points"
                desc="Total Points pool"
                value={formatBigValue(myHighLeveragePoint, {
                  decimals: 2,
                  displayDecimals: 2,
                })}
                totalValue={formatBigValue(totalHighLeveragePoint, {
                  decimals: 2,
                  displayDecimals: 2,
                })}
                explorerUrl={
                  degenGenesisPointsAddress
                    ? getExplorerLink(
                        degenGenesisPointsAddress,
                        'token',
                        chainId,
                      )
                    : undefined
                }
              />
            </Box>
          </Grid>

          {/* boost row */}
          <Flex
            align="center"
            gap="16px"
            flexWrap="wrap"
            borderTop="1px solid"
            borderTopColor="v2.border.default"
            px={{ base: '16px', tablet: '20px' }}
            py="13px"
          >
            <Text textStyle="v2.label" color="v2.text.muted">
              Previous Epoch Boost Point
            </Text>
            <HStack gap="6px">
              <styled.img
                src="/icons/general-point.svg"
                alt=""
                w="18px"
                h="18px"
              />
              <BigIntAmount
                textStyle="numbers.default"
                color="v2.text.primary"
                opts={{
                  prefix: '',
                  decimals: 18,
                  displayDecimals: 2,
                }}
                value={data?.userData.teamBoostGeneralClaimable || 0n}
              />
            </HStack>
            <HStack gap="6px">
              <styled.img
                src="/icons/high-leverage.svg"
                alt=""
                w="18px"
                h="18px"
              />
              <BigIntAmount
                textStyle="numbers.default"
                color="v2.text.primary"
                opts={{
                  prefix: '',
                  decimals: 18,
                  displayDecimals: 2,
                }}
                value={data?.userData.teamBoostDegenClaimable || 0n}
              />
            </HStack>
            <Flex
              align="center"
              gap="12px"
              ml={{ tablet: 'auto' }}
              w={{ base: '100%', tablet: 'auto' }}
              flexWrap="wrap"
            >
              {noClaimablePreviousBoost ? null : (
                <Text
                  textStyle="numbers.xsmall"
                  color="v2.text.subtle"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  Expire:{' '}
                  {formatDurationShort(timeUntilNextDayEpoch, {
                    showZeroSeconds: true,
                  })}
                </Text>
              )}
              <Button
                disabled={noClaimablePreviousBoost}
                h="36px"
                px="18px"
                w={{ base: '100%', tablet: 'auto' }}
              >
                Open a position to claim
              </Button>
            </Flex>
          </Flex>
        </Panel>

        {/* ---- Referral ---- */}
        <Panel>
          {/* mobile actions */}
          <Flex
            display={{ base: 'flex', desktop: 'none' }}
            px="12px"
            py="10px"
            borderBottom="1px solid"
            borderBottomColor="v2.border.hairline"
          >
            <RequireConnectionWrapper justifyContent="flex-start" w="100%">
              <ReferralActions
                createCodeDisabled={userVolumeUSD < createCodeMinVolume}
              />
            </RequireConnectionWrapper>
          </Flex>

          <Tabs.Root defaultValue="inviter" variant="textLine">
            <Tabs.List
              px="8px"
              gap="16px"
              alignItems="center"
              borderBottom="1px solid"
              borderColor="v2.border.default"
            >
              <Tabs.Trigger
                value="inviter"
                h="42px"
                px="12px"
                textStyle="v2.ui"
              >
                As the inviter
              </Tabs.Trigger>
              <Tabs.Trigger
                value="invitee"
                h="42px"
                px="12px"
                textStyle="v2.ui"
              >
                As the invitee
              </Tabs.Trigger>
              <Tabs.Indicator />

              <Box
                display={{ base: 'none', desktop: 'flex' }}
                justifyContent="flex-end"
                alignItems="center"
                w="100%"
                pr="8px"
              >
                <RequireConnectionWrapper justifyContent="flex-end" w="100%">
                  <ReferralActions
                    createCodeDisabled={userVolumeUSD < createCodeMinVolume}
                  />
                </RequireConnectionWrapper>
              </Box>
            </Tabs.List>

            <Tabs.Content style={{ paddingTop: 0 }} value="inviter">
              <Grid
                gridTemplateColumns={{
                  base: '1fr',
                  desktop: '300px minmax(0, 1fr)',
                }}
                gap={0}
                alignItems="stretch"
              >
                <Box
                  py="6px"
                  pb="10px"
                  borderRight={{ desktop: '1px solid' }}
                  borderRightColor={{ desktop: 'v2.border.default' }}
                  borderBottom={{ base: '1px solid', desktop: 'none' }}
                  borderBottomColor="v2.border.default"
                >
                  <SectionLabel px="16px" pt="10px" pb="4px">
                    Basic Info
                  </SectionLabel>
                  {basicInfoRows(leaderTeamData, isLeaderTeamCreated)}
                </Box>

                <Flex direction="column" minW={0}>
                  <SectionLabel px="16px" pt="10px" pb="6px">
                    Invitees
                  </SectionLabel>
                  <TeamMembers isLeaderTeamCreated={isLeaderTeamCreated} />
                </Flex>
              </Grid>
            </Tabs.Content>

            <Tabs.Content style={{ paddingTop: 0 }} value="invitee">
              <Grid
                gridTemplateColumns={{
                  base: '1fr',
                  desktop: '300px minmax(0, 1fr)',
                }}
                gap={0}
                alignItems="stretch"
              >
                <Box
                  py="6px"
                  pb="10px"
                  borderRight={{ desktop: '1px solid' }}
                  borderRightColor={{ desktop: 'v2.border.default' }}
                  borderBottom={{ base: '1px solid', desktop: 'none' }}
                  borderBottomColor="v2.border.default"
                >
                  <SectionLabel px="16px" pt="10px" pb="4px">
                    Basic Info
                  </SectionLabel>
                  {basicInfoRows(memberTeamData, isMemberTeamJoined)}
                </Box>

                <Flex direction="column" minW={0}>
                  <SectionLabel px="16px" pt="10px" pb="6px">
                    Boost History
                  </SectionLabel>
                  <TeamBoostHistories isMemberTeamJoined={isMemberTeamJoined} />
                </Flex>
              </Grid>
            </Tabs.Content>
          </Tabs.Root>
        </Panel>

        {/* ---- Team leaderboard ---- */}
        <Panel>
          <Flex
            justify="space-between"
            align="center"
            gap="12px"
            px="16px"
            py="12px"
            borderBottom="1px solid"
            borderBottomColor="v2.border.hairline"
          >
            <SectionLabel>Team Leaderboard · Epoch (24H)</SectionLabel>
            <Text
              fontFamily="Inter"
              fontSize="10.5px"
              fontWeight="500"
              color="v2.text.subtle"
              css={{ fontVariantNumeric: 'tabular-nums' }}
            >
              Epoch {Number(currentEpoch)}
            </Text>
          </Flex>
          <TeamLeaderBoard epoch={Number(currentEpoch)} />
        </Panel>

        {/* ---- Page foot ---- */}
        <Flex
          align="center"
          gap="18px"
          flexWrap="wrap"
          fontFamily="Inter"
          fontSize="11px"
          color="v2.text.subtle"
        >
          <span>Points settle per epoch · $LV distribution at season close</span>
        </Flex>
      </Flex>
    </Flex>
  );
};

/* ---------------------------------------------------------------- */
/* Team leaderboard                                                 */
/* ---------------------------------------------------------------- */

const leaderboardColumns = '1.6fr 1.1fr 1fr 1fr 0.8fr';

const TeamLeaderBoard = ({ epoch }: { epoch: number }) => {
  const pageSize = 20;
  const { query, setPage } = useTeamLeaderboardQuery(epoch, pageSize);

  return (
    <Stack gap={0}>
      <Box overflowX="auto">
        <Box minW="680px">
          <Grid
            gridTemplateColumns={leaderboardColumns}
            columnGap="12px"
            alignItems="center"
            h="30px"
            px="16px"
            borderBottom="1px solid"
            borderBottomColor="v2.border.hairline"
          >
            <Box css={tableHeaderCellCss}>Name</Box>
            <Flex justify="flex-end" align="center" gap="4px">
              <Box css={tableHeaderCellCss} textAlign="right">
                Current Epoch Volume
              </Box>
              <TextTooltip
                content={
                  <Stack gap={1}>
                    <Text textStyle="text.small">
                      Normal leverage: Open and close trade volume are counted
                    </Text>
                    <Text textStyle="text.small">
                      High leverage: Only close trade volume is counted
                    </Text>
                  </Stack>
                }
              >
                <Icon boxSize="12px" color="v2.text.subtle">
                  <Info />
                </Icon>
              </TextTooltip>
            </Flex>
            <Box css={tableHeaderCellCss} textAlign="right">
              General Point
            </Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              High Leverage Point
            </Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              Team Boost
            </Box>
          </Grid>
          {query.data?.content.map((item, index) => {
            const rank =
              (query.data?.pageNumber || 0) * pageSize + index + 1;
            const boost = calculateTeamBoost(BigInt(item.currentEpochVolume));

            return (
              <Grid
                key={index}
                gridTemplateColumns={leaderboardColumns}
                columnGap="12px"
                alignItems="center"
                h="38px"
                px="16px"
                borderBottom="1px solid"
                borderBottomColor="v2.border.hairline"
                _last={{ borderBottom: 'none' }}
                _hover={{ bg: 'v2.bg.surface1' }}
              >
                <HStack gap="9px" minW={0}>
                  <RankChip rank={rank} />
                  <Text
                    textStyle="v2.label"
                    color="v2.text.primary"
                    truncate
                  >
                    {item.teamName}
                  </Text>
                </HStack>
                <BigIntAmount
                  textStyle="numbers.small"
                  color="v2.text.secondary"
                  textAlign="right"
                  opts={{
                    decimals: 18,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.currentEpochVolume)}
                />
                <BigIntAmount
                  textStyle="numbers.small"
                  color={
                    BigInt(item.currentGeneralPoints) > 0n
                      ? 'v2.text.secondary'
                      : 'v2.text.subtle'
                  }
                  textAlign="right"
                  opts={{
                    decimals: 2,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.currentGeneralPoints)}
                />
                <BigIntAmount
                  textStyle="numbers.small"
                  color={
                    BigInt(item.currentDegenPoints) > 0n
                      ? 'v2.text.secondary'
                      : 'v2.text.subtle'
                  }
                  textAlign="right"
                  opts={{
                    decimals: 2,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.currentDegenPoints)}
                />
                <Text
                  textStyle="numbers.small"
                  color={
                    boost !== '1.0' ? 'v2.long.default' : 'v2.text.secondary'
                  }
                  textAlign="right"
                >
                  {boost}×
                </Text>
              </Grid>
            );
          })}
        </Box>
      </Box>
      {(query.data?.totalPages || 0) > 1 && (
        <Flex justify="flex-end" p="12px">
          <Pagination
            size="xsmall"
            page={(query.data?.pageNumber || 0) + 1}
            count={query.data?.totalElements || 0}
            pageSize={pageSize}
            onPageChange={(page) => {
              setPage(page.page);
            }}
            defaultPage={1}
          />
        </Flex>
      )}
    </Stack>
  );
};

/* ---------------------------------------------------------------- */
/* Boost history (invitee tab)                                      */
/* ---------------------------------------------------------------- */

const boostHistoryColumns = '1fr 1fr 1fr';

const TeamBoostHistories = ({
  isMemberTeamJoined,
}: {
  isMemberTeamJoined: boolean;
}) => {
  const { address } = useAccount();
  const pageSize = 3;
  const { query, setPage } = useTeamBoostHistoryQuery(address, pageSize);

  return (
    <Flex direction="column" flex="1" justify="space-between" gap={0}>
      <Box overflowX="auto">
        <Box minW="420px">
          <Grid
            gridTemplateColumns={boostHistoryColumns}
            columnGap="12px"
            alignItems="center"
            h="30px"
            px="16px"
            borderBottom="1px solid"
            borderBottomColor="v2.border.hairline"
          >
            <Box css={tableHeaderCellCss}>Epoch</Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              General Point Reward
            </Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              High Leverage Point Reward
            </Box>
          </Grid>
          {query.data?.content?.length ? (
            query.data.content.map((item, index) => (
              <Grid
                key={index}
                gridTemplateColumns={boostHistoryColumns}
                columnGap="12px"
                alignItems="center"
                h="38px"
                px="16px"
                borderBottom="1px solid"
                borderBottomColor="v2.border.hairline"
                _last={{ borderBottom: 'none' }}
                _hover={{ bg: 'v2.bg.surface1' }}
              >
                <Text textStyle="numbers.small" color="v2.text.primary">
                  {item.epoch}
                </Text>
                <BigIntAmount
                  textStyle="numbers.small"
                  color={
                    BigInt(item.generalTeamBoostPoints) > 0n
                      ? 'v2.long.default'
                      : 'v2.text.subtle'
                  }
                  textAlign="right"
                  opts={{
                    decimals: 2,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.generalTeamBoostPoints)}
                />
                <BigIntAmount
                  textStyle="numbers.small"
                  color={
                    BigInt(item.degenTeamBoostPoints) > 0n
                      ? 'v2.long.default'
                      : 'v2.text.subtle'
                  }
                  textAlign="right"
                  opts={{
                    decimals: 2,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.degenTeamBoostPoints)}
                />
              </Grid>
            ))
          ) : (
            <NoDataPlaceholder action={!isMemberTeamJoined ? 'join' : 'none'} />
          )}
        </Box>
      </Box>
      {(query.data?.totalPages || 0) > 1 && (
        <Flex justify="flex-end" p="12px">
          <Pagination
            size="xsmall"
            page={(query.data?.pageNumber || 0) + 1}
            count={query.data?.totalElements || 0}
            pageSize={pageSize}
            onPageChange={(page) => {
              setPage(page.page);
            }}
            defaultPage={1}
          />
        </Flex>
      )}
    </Flex>
  );
};

/* ---------------------------------------------------------------- */
/* Invitees (inviter tab)                                           */
/* ---------------------------------------------------------------- */

const inviteesColumns = '1.3fr 0.9fr 1fr 1fr 1.2fr';

const TeamMembers = ({
  isLeaderTeamCreated,
}: {
  isLeaderTeamCreated: boolean;
}) => {
  const { address } = useAccount();
  const pageSize = 3;
  const { query, setPage } = useTeamMemberQuery(address, pageSize);

  return (
    <Flex direction="column" flex="1" justify="space-between" gap={0}>
      <Box overflowX="auto">
        <Box minW="640px">
          <Grid
            gridTemplateColumns={inviteesColumns}
            columnGap="12px"
            alignItems="center"
            h="30px"
            px="16px"
            borderBottom="1px solid"
            borderBottomColor="v2.border.hairline"
          >
            <Box css={tableHeaderCellCss}>Address</Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              Date Joined
            </Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              Total volume
            </Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              General Point Reward
            </Box>
            <Box css={tableHeaderCellCss} textAlign="right">
              High Leverage Point Reward
            </Box>
          </Grid>
          {query.data?.content?.length ? (
            query.data.content.map((item, index) => (
              <Grid
                key={index}
                gridTemplateColumns={inviteesColumns}
                columnGap="12px"
                alignItems="center"
                h="38px"
                px="16px"
                borderBottom="1px solid"
                borderBottomColor="v2.border.hairline"
                _last={{ borderBottom: 'none' }}
                _hover={{ bg: 'v2.bg.surface1' }}
              >
                <Text textStyle="numbers.small" color="v2.text.primary">
                  {shortenAddress({
                    address: item.member,
                  })}
                </Text>
                <Text
                  textStyle="numbers.small"
                  color="v2.text.secondary"
                  textAlign="right"
                >
                  {formatDate(item.joinDate)}
                </Text>
                <BigIntAmountUsd
                  textStyle="numbers.small"
                  color="v2.text.secondary"
                  textAlign="right"
                  opts={{
                    decimals: 18,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.cumulativeVolumeUSD)}
                />
                <BigIntAmount
                  textStyle="numbers.small"
                  color={
                    BigInt(item.cumulativeGeneralPointsToLeader) > 0n
                      ? 'v2.long.default'
                      : 'v2.text.subtle'
                  }
                  textAlign="right"
                  opts={{
                    decimals: 2,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.cumulativeGeneralPointsToLeader)}
                />
                <BigIntAmount
                  textStyle="numbers.small"
                  color={
                    BigInt(item.cumulativeDegenPointsToLeader) > 0n
                      ? 'v2.long.default'
                      : 'v2.text.subtle'
                  }
                  textAlign="right"
                  opts={{
                    decimals: 2,
                    displayDecimals: 2,
                  }}
                  value={BigInt(item.cumulativeDegenPointsToLeader)}
                />
              </Grid>
            ))
          ) : (
            <NoDataPlaceholder
              action={!isLeaderTeamCreated ? 'invite' : 'none'}
            />
          )}
        </Box>
      </Box>
      {(query.data?.totalPages || 0) > 1 && (
        <Flex justify="flex-end" p="12px">
          <Pagination
            size="xsmall"
            page={(query.data?.pageNumber || 0) + 1}
            count={query.data?.totalElements || 0}
            pageSize={pageSize}
            onPageChange={(page) => {
              setPage(page.page);
            }}
            defaultPage={1}
          />
        </Flex>
      )}
    </Flex>
  );
};

/* ---------------------------------------------------------------- */
/* Referral actions (Enter Code / Create-Share Code dialogs)        */
/* ---------------------------------------------------------------- */

const ReferralActions = ({
  createCodeDisabled,
  onlyShare = false,
  customShareTrigger,
  onlyJoin = false,
  customJoinTrigger,
}: {
  createCodeDisabled?: boolean;
  onlyShare?: boolean;
  customShareTrigger?: React.ReactNode;
  onlyJoin?: boolean;
  customJoinTrigger?: React.ReactNode;
}) => {
  const { address } = useAccount();
  const [isCreateCodeOpen, setIsCreateCodeOpen] = useState<boolean>(false);
  const [isJoinOpen, setIsJoinOpen] = useState<boolean>(false);
  const [teamName, setTeamName] = useState<string>('');
  const [teamCode, setTeamCode] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const { refetch } = useGenesisAirdropUIData(address);
  const { refetch: refetchUserTeamInfo, data: userTeamInfo } =
    useUserTeamInfo(address);
  const { writeContract, isPending } = useCreateCode({
    teamName,
    code: teamCode,
    onSettled: () => {
      setIsCreateCodeOpen(false);
      refetch({ cancelRefetch: false });
      refetchUserTeamInfo({ cancelRefetch: false });
    },
  });
  const { writeContract: joinTeam, isPending: isJoining } = useJoinTeam({
    code,
    onSettled: () => {
      setIsJoinOpen(false);
      refetch({ cancelRefetch: false });
      refetchUserTeamInfo({ cancelRefetch: false });
    },
  });

  const leaderTeamData = userTeamInfo?.leaderTeam || teamDataDefault;
  const memberTeamData = userTeamInfo?.memberTeam || teamDataDefault;
  const hasLeaderTeam = leaderTeamData.teamMemberCount > 0;
  const hasMemberTeam = memberTeamData.teamMemberCount > 0;
  const isShareMode = hasLeaderTeam || hasMemberTeam;

  const sanitizeCreateCodeInput = (input: string) => {
    return input
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 10);
  };

  const isTeamNameValid = teamName.trim().length > 0;
  const isTeamCodeValid = /^[A-Z0-9]{3,10}$/.test(teamCode);
  const isCreateFormInvalid = !isTeamNameValid || !isTeamCodeValid;

  const onCopyLink = async (codeToShare?: string) => {
    try {
      const origin = window.location.origin;
      const codeToUse = codeToShare || leaderTeamData.code;
      const urlToCopy = codeToUse
        ? `${origin}?code=${encodeURIComponent(codeToUse)}`
        : window.location.href;
      await navigator.clipboard.writeText(urlToCopy);
      basicToaster.success({ title: 'Copied to clipboard' });
    } catch {}
  };

  return (
    <HStack gap={2}>
      {!onlyShare && (
        <Dialog.Root
          open={isJoinOpen}
          onOpenChange={(details) => {
            setIsJoinOpen(details.open);
          }}
        >
          <Dialog.Trigger asChild>
            {customJoinTrigger || (
              <Button
                size="small"
                variant="secondary"
                h="30px"
                px="12px"
                disabled={memberTeamData.teamMemberCount > 0}
              >
                Enter Code
              </Button>
            )}
          </Dialog.Trigger>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content p={6} w={{ base: '92vw', tablet: '400px' }}>
              <Dialog.CloseTrigger position="absolute" top={4} right={4} />
              <Stack gap={4}>
                <HStack justifyContent="space-between">
                  <Dialog.Title p={0}>Enter Code</Dialog.Title>
                  <Dialog.CloseTrigger boxShadow="none" bg="none">
                    <Icon w="20px" h="20px">
                      <X />
                    </Icon>
                  </Dialog.CloseTrigger>
                </HStack>
                <Dialog.Description
                  textStyle="text.small"
                  color="v2.text.muted"
                >
                  Enter the code to join the other team&apos;s invitation, and
                  both you and the team members will receive boosted points.
                </Dialog.Description>
                <Input
                  placeholder="Enter Code"
                  bg="v2.bg.surface1"
                  p={3}
                  borderRadius="2px"
                  value={code}
                  maxLength={10}
                  onChange={(e) =>
                    setCode(sanitizeCreateCodeInput(e.target.value))
                  }
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text');
                    setCode(sanitizeCreateCodeInput(text));
                  }}
                />
                <Button
                  w="full"
                  variant="primary"
                  onClick={joinTeam}
                  loading={isJoining}
                >
                  Confirm
                </Button>
              </Stack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}

      {!onlyJoin && (
        <Dialog.Root
          open={isCreateCodeOpen}
          onOpenChange={(details) => {
            setIsCreateCodeOpen(details.open);
          }}
        >
          <Dialog.Trigger asChild>
            {customShareTrigger || (
              <Button size="small" variant="primary" h="30px" px="14px">
                {isShareMode ? 'Share Code' : 'Create Code'}
              </Button>
            )}
          </Dialog.Trigger>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content p={6} w={{ base: '92vw', tablet: '400px' }}>
              <Dialog.CloseTrigger position="absolute" top={4} right={4} />
              <Stack gap={4}>
                <HStack justifyContent="space-between">
                  <Dialog.Title p={0}>
                    {isShareMode ? 'Share Code' : 'Create Code'}
                  </Dialog.Title>
                  <Dialog.CloseTrigger boxShadow="none" bg="none">
                    <Icon w="20px" h="20px">
                      <X />
                    </Icon>
                  </Dialog.CloseTrigger>
                </HStack>
                <Stack gap={3}>
                  <HStack
                    justify="space-between"
                    bg="v2.bg.surface1"
                    p={3}
                    borderRadius="2px"
                  >
                    <Text textStyle="text.small" color="v2.text.muted">
                      Account
                    </Text>
                    <Text
                      textStyle="numbers.small"
                      color="v2.text.primary"
                    >
                      {shortenAddress({
                        address: address || zeroAddress,
                      })}
                    </Text>
                  </HStack>

                  {isShareMode ? (
                    <>
                      {hasLeaderTeam && (
                        <Stack gap={2}>
                          <Text
                            textStyle="text.small"
                            color="v2.text.secondary"
                          >
                            My Own Team
                          </Text>
                          <HStack
                            justify="space-between"
                            bg="v2.bg.surface1"
                            p={3}
                            borderRadius="2px"
                          >
                            <Text
                              textStyle="text.small"
                              color="v2.text.muted"
                            >
                              Team Name
                            </Text>
                            <Text
                              textStyle="text.small"
                              color="v2.text.primary"
                            >
                              {leaderTeamData.teamName}
                            </Text>
                          </HStack>
                          <HStack
                            justify="space-between"
                            bg="v2.bg.surface1"
                            p={3}
                            borderRadius="2px"
                          >
                            <Text
                              textStyle="text.small"
                              color="v2.text.muted"
                            >
                              Code
                            </Text>
                            <Text
                              textStyle="numbers.small"
                              color="v2.long.default"
                            >
                              {leaderTeamData.code}
                            </Text>
                          </HStack>
                          <Button
                            variant="secondary"
                            onClick={() => onCopyLink(leaderTeamData.code)}
                          >
                            <HStack gap={2}>
                              <Icon boxSize="5">
                                <ShareLink />
                              </Icon>
                              <span>Copy Link</span>
                            </HStack>
                          </Button>
                        </Stack>
                      )}

                      {hasMemberTeam && (
                        <Stack gap={2}>
                          <Text
                            textStyle="text.small"
                            color="v2.text.secondary"
                          >
                            My Joined Team
                          </Text>
                          <HStack
                            justify="space-between"
                            bg="v2.bg.surface1"
                            p={3}
                            borderRadius="2px"
                          >
                            <Text
                              textStyle="text.small"
                              color="v2.text.muted"
                            >
                              Team Name
                            </Text>
                            <Text
                              textStyle="text.small"
                              color="v2.text.primary"
                            >
                              {memberTeamData.teamName}
                            </Text>
                          </HStack>
                          <HStack
                            justify="space-between"
                            bg="v2.bg.surface1"
                            p={3}
                            borderRadius="2px"
                          >
                            <Text
                              textStyle="text.small"
                              color="v2.text.muted"
                            >
                              Code
                            </Text>
                            <Text
                              textStyle="numbers.small"
                              color="v2.long.default"
                            >
                              {memberTeamData.code}
                            </Text>
                          </HStack>
                          <Button
                            variant="secondary"
                            onClick={() => onCopyLink(memberTeamData.code)}
                          >
                            <HStack gap={2}>
                              <Icon boxSize="5">
                                <ShareLink />
                              </Icon>
                              <span>Copy Link</span>
                            </HStack>
                          </Button>
                        </Stack>
                      )}

                      {hasMemberTeam && !hasLeaderTeam && (
                        <Stack mt={3} gap={2}>
                          <Text
                            textStyle="text.small"
                            color="v2.text.secondary"
                          >
                            Create Your Own Team
                          </Text>
                          {!createCodeDisabled ? (
                            <>
                              <Input
                                textStyle="text.small"
                                placeholder="Enter Team Name"
                                bg="v2.bg.surface1"
                                p={3}
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                borderRadius="2px"
                              />
                              <Input
                                textStyle="text.small"
                                placeholder="Enter Code (3-10, A-Z and 0-9)"
                                bg="v2.bg.surface1"
                                p={3}
                                value={teamCode}
                                onChange={(e) =>
                                  setTeamCode(
                                    sanitizeCreateCodeInput(e.target.value),
                                  )
                                }
                                borderRadius="2px"
                              />
                              <Button
                                onClick={writeContract}
                                loading={isPending}
                                variant="primary"
                                disabled={
                                  createCodeDisabled || isCreateFormInvalid
                                }
                                w="full"
                              >
                                Create Code
                              </Button>
                            </>
                          ) : (
                            <Text
                              textStyle="text.small"
                              color="v2.text.primary"
                            >
                              You can create a code after &gt; $10,000 in volume
                            </Text>
                          )}
                        </Stack>
                      )}
                    </>
                  ) : (
                    <>
                      <Input
                        textStyle="text.small"
                        disabled={createCodeDisabled}
                        placeholder="Enter Team Name"
                        bg="v2.bg.surface1"
                        p={3}
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        borderRadius="2px"
                      />
                      <Input
                        textStyle="text.small"
                        disabled={createCodeDisabled}
                        placeholder="Enter Code (3-10, A-Z and 0-9)"
                        bg="v2.bg.surface1"
                        p={3}
                        value={teamCode}
                        onChange={(e) =>
                          setTeamCode(sanitizeCreateCodeInput(e.target.value))
                        }
                        borderRadius="2px"
                      />
                      {createCodeDisabled && (
                        <Text
                          textStyle="text.small"
                          color="v2.text.primary"
                        >
                          You can create a code after &gt; $10,000 in volume
                        </Text>
                      )}
                    </>
                  )}
                </Stack>
                {!isShareMode && (
                  <Button
                    variant="primary"
                    onClick={writeContract}
                    loading={isPending}
                    disabled={createCodeDisabled || isCreateFormInvalid}
                    w="full"
                  >
                    Confirm
                  </Button>
                )}
              </Stack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}
    </HStack>
  );
};

/* ---------------------------------------------------------------- */
/* Points cell (points strip)                                       */
/* ---------------------------------------------------------------- */

const PointsCell = ({
  title,
  value,
  desc,
  totalValue,
  iconSrc,
  explorerUrl,
}: {
  title: string;
  desc: string;
  value: string;
  totalValue: string;
  iconSrc?: string;
  explorerUrl?: string;
}) => {
  return (
    <Flex
      direction="column"
      gap="5px"
      p={{ base: '16px', tablet: '18px 20px' }}
    >
      <HStack gap="8px">
        {iconSrc ? (
          <styled.img src={iconSrc} alt="" w="22px" h="22px" flexShrink={0} />
        ) : null}
        <SectionLabel>{title}</SectionLabel>
        {explorerUrl && (
          <TextTooltip
            content="View on Explorer"
            triggerCss={{
              textDecoration: 'none',
            }}
            contentCss={{
              p: '4px 6px',
            }}
          >
            <Icon
              onClick={() => {
                window.open(explorerUrl, '_blank');
              }}
              cursor="pointer"
              boxSize="12px"
              color="v2.text.subtle"
              _hover={{ color: 'v2.text.primary' }}
            >
              <LinkExternal />
            </Icon>
          </TextTooltip>
        )}
      </HStack>

      <Text
        fontFamily="Inter"
        fontWeight="500"
        fontSize={{ base: '22px', tablet: '28px' }}
        lineHeight="1.25"
        letterSpacing="-0.015em"
        color="v2.text.primary"
        css={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>

      <Flex
        justify="space-between"
        align="center"
        gap="12px"
        borderTop="1px solid"
        borderTopColor="v2.border.hairline"
        mt="8px"
        pt="9px"
      >
        <Text textStyle="v2.meta" color="v2.text.subtle" fontWeight="400">
          {desc}
        </Text>
        <Text textStyle="numbers.xsmall" color="v2.text.secondary">
          {totalValue}
        </Text>
      </Flex>
    </Flex>
  );
};

/* ---------------------------------------------------------------- */
/* Empty state                                                      */
/* ---------------------------------------------------------------- */

const NoDataPlaceholder = ({
  action = 'none',
}: {
  action?: 'invite' | 'join' | 'none';
}) => {
  const { address } = useAccount();
  const { data } = useGenesisAirdropUIData(address);
  const userVolumeUSD = data?.userData.userCumulativeVolumeUSD || 0n;
  const createCodeMinVolume = parseUnits('10000', 18);

  return (
    <Flex direction="column" align="center" justify="center" py="32px" gap="14px" w="full">
      <Text textStyle="v2.label" color="v2.text.subtle">
        No data to show
      </Text>
      {action === 'invite' && (
        <ReferralActions
          createCodeDisabled={userVolumeUSD < createCodeMinVolume}
          onlyShare
          customShareTrigger={<Button variant="primary">Invite friends</Button>}
        />
      )}
      {action === 'join' && (
        <ReferralActions
          createCodeDisabled={userVolumeUSD < createCodeMinVolume}
          onlyJoin
          customJoinTrigger={<Button variant="primary">Join Team</Button>}
        />
      )}
    </Flex>
  );
};
